import { collection, doc, setDoc, getDoc, getDocs, deleteDoc, query, where, orderBy, onSnapshot, Unsubscribe, deleteField } from 'firebase/firestore';
import { db } from './firebase';
import { Song, Playlist } from '../types';
import authService from './authService';

class MusicService {
  private readonly SONGS_COLLECTION = 'userSongs';
  private readonly PLAYLISTS_COLLECTION = 'userPlaylists';

  private removeUndefined(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) return obj;
    if (typeof obj !== 'object') return obj;
    const result: any = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return; // skip undefined fields
      }
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result[key] = this.removeUndefined(value);
      } else {
        result[key] = value;
      }
    });
    return result;
  }

  private prepareUpdatePayload(updates: Partial<Song>): any {
    const payload: any = {};
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined) {
        // omit undefined entirely
        return;
      }
      if (value === null) {
        // delete the field from Firestore document
        payload[key] = deleteField();
        return;
      }
      payload[key] = value as any;
    });
    return payload;
  }

  // Songs
  async saveSong(song: Song): Promise<void> {
    const user = authService.getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    const songDoc = doc(db, this.SONGS_COLLECTION, song.id);
    const payload = this.removeUndefined({
      ...song,
      userId: user.uid,
      updatedAt: Date.now()
    });
    await setDoc(songDoc, payload);
  }

  async getSong(songId: string): Promise<Song | null> {
    const songDoc = doc(db, this.SONGS_COLLECTION, songId);
    const snapshot = await getDoc(songDoc);
    
    if (!snapshot.exists()) return null;
    
    const data = snapshot.data();
    return {
      id: data.id,
      name: data.name,
      fileName: data.fileName,
      storagePath: data.storagePath,
      downloadUrl: data.downloadUrl,
      duration: data.duration,
      size: data.size,
      addedAt: data.addedAt,
      analysis: data.analysis
    };
  }

  async getUserSongs(): Promise<Song[]> {
    const user = authService.getCurrentUser();
    if (!user) return [];

    const q = query(
      collection(db, this.SONGS_COLLECTION),
      where('userId', '==', user.uid),
      orderBy('addedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: data.id,
        name: data.name,
        fileName: data.fileName,
        storagePath: data.storagePath,
        downloadUrl: data.downloadUrl,
        duration: data.duration,
        size: data.size,
        addedAt: data.addedAt,
        analysis: data.analysis
      };
    });
  }

  async deleteSong(songId: string): Promise<void> {
    const songDoc = doc(db, this.SONGS_COLLECTION, songId);
    await deleteDoc(songDoc);
  }

  async updateSong(songId: string, updates: Partial<Song>): Promise<void> {
    const user = authService.getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    const songDoc = doc(db, this.SONGS_COLLECTION, songId);
    const payload = {
      ...this.prepareUpdatePayload(updates),
      updatedAt: Date.now()
    };
    await setDoc(songDoc, payload, { merge: true });
  }

  async bulkDeleteSongs(songIds: string[]): Promise<void> {
    const deletePromises = songIds.map(songId => this.deleteSong(songId));
    await Promise.all(deletePromises);
  }

  async bulkAddToPlaylist(songIds: string[], playlistId: string): Promise<void> {
    const playlist = await this.getPlaylist(playlistId);
    if (!playlist) throw new Error('Playlist not found');

    const newSongIds = [...new Set([...playlist.songIds, ...songIds])];
    await this.updatePlaylist(playlistId, { songIds: newSongIds });
  }

  async bulkRemoveFromPlaylist(songIds: string[], playlistId: string): Promise<void> {
    const playlist = await this.getPlaylist(playlistId);
    if (!playlist) throw new Error('Playlist not found');

    const newSongIds = playlist.songIds.filter(id => !songIds.includes(id));
    await this.updatePlaylist(playlistId, { songIds: newSongIds });
  }

  // Playlists
  async savePlaylist(playlist: Playlist): Promise<void> {
    const user = authService.getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    const playlistDoc = doc(db, this.PLAYLISTS_COLLECTION, playlist.id);
    await setDoc(playlistDoc, {
      ...playlist,
      userId: user.uid,
      updatedAt: Date.now()
    });
  }

  async getPlaylist(playlistId: string): Promise<Playlist | null> {
    const playlistDoc = doc(db, this.PLAYLISTS_COLLECTION, playlistId);
    const snapshot = await getDoc(playlistDoc);
    
    if (!snapshot.exists()) return null;
    
    const data = snapshot.data();
    return {
      id: data.id,
      name: data.name,
      songIds: data.songIds,
      createdAt: data.createdAt
    };
  }

  async getUserPlaylists(): Promise<Playlist[]> {
    const user = authService.getCurrentUser();
    if (!user) return [];

    const q = query(
      collection(db, this.PLAYLISTS_COLLECTION),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: data.id,
        name: data.name,
        songIds: data.songIds,
        createdAt: data.createdAt
      };
    });
  }

  async deletePlaylist(playlistId: string): Promise<void> {
    const playlistDoc = doc(db, this.PLAYLISTS_COLLECTION, playlistId);
    await deleteDoc(playlistDoc);
  }

  async updatePlaylist(playlistId: string, updates: Partial<Playlist>): Promise<void> {
    const playlistDoc = doc(db, this.PLAYLISTS_COLLECTION, playlistId);
    await setDoc(playlistDoc, {
      ...updates,
      updatedAt: Date.now()
    }, { merge: true });
  }

  // Real-time listeners
  subscribeToUserSongs(callback: (songs: Song[]) => void): Unsubscribe {
    const user = authService.getCurrentUser();
    if (!user) {
      callback([]);
      return () => {};
    }

    const q = query(
      collection(db, this.SONGS_COLLECTION),
      where('userId', '==', user.uid),
      orderBy('addedAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const songs = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: data.id,
          name: data.name,
          fileName: data.fileName,
          storagePath: data.storagePath,
          downloadUrl: data.downloadUrl,
          duration: data.duration,
          size: data.size,
          addedAt: data.addedAt,
          analysis: data.analysis
        };
      });
      callback(songs);
    }, (error) => {
      console.error('Error listening to songs:', error);
      callback([]);
    });
  }

  subscribeToUserPlaylists(callback: (playlists: Playlist[]) => void): Unsubscribe {
    const user = authService.getCurrentUser();
    if (!user) {
      callback([]);
      return () => {};
    }

    const q = query(
      collection(db, this.PLAYLISTS_COLLECTION),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const playlists = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: data.id,
          name: data.name,
          songIds: data.songIds,
          createdAt: data.createdAt
        };
      });
      callback(playlists);
    }, (error) => {
      console.error('Error listening to playlists:', error);
      callback([]);
    });
  }
}

export default new MusicService();
